import { CheckOutlined, CloseOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, DatePicker, Input, Modal, Select, Space, Table, Tag, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageTitle from 'components/page-title';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import ItemFormModal from './components/item-form-modal';
import NoAccess from './components/no-access';
import { PolicyData, RewardItem, RewardRow } from './types';
import { ITEM_STATUS_COLOR, ITEM_STATUS_LABEL, ITEM_TYPES, ITEM_TYPE_LABEL, currentUtcMonth, hoursText, previousUtcMonth, rewardErrorMessage, vnd } from './utils';
import styles from './style.module.scss';

const isMonth = (v: string | null): v is string => !!v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);

/** Thưởng Online → Khoản nhập tay (docs/thuong-online/02 §7). */
const OnlineRewardItemsPage = () => {
  const { loaded, isAdmin, userId, canViewOnlineReward, canEnterOnlineReward } = useCurrentRoles();
  const [params, setParams] = useSearchParams();
  const month = isMonth(params.get('month')) ? (params.get('month') as string) : previousUtcMonth();
  const uid = params.get('uid');
  const [status, setStatus] = useState<string | undefined>();
  const [type, setType] = useState<string | undefined>();
  const [items, setItems] = useState<RewardItem[]>([]);
  const [creators, setCreators] = useState<RewardRow[]>([]);
  const [presets, setPresets] = useState<PolicyData['presets'] | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RewardItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    AxiosUtils.get(API.onlineRewardItems, { month, status, type, uid: uid || undefined })
      .then((res: any) => setItems(res?.data ?? []))
      .catch((err) => setError(rewardErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [month, status, type, uid]);

  useEffect(() => {
    if (!canViewOnlineReward) return;
    load();
  }, [canViewOnlineReward, load]);

  useEffect(() => {
    if (!canEnterOnlineReward) return;
    AxiosUtils.get(`${API.onlineRewardMonths}/${month}`).then((res: any) => setCreators(res?.data?.rows ?? [])).catch(() => setCreators([]));
    AxiosUtils.get(API.onlineRewardPolicies).then((res: any) => setPresets(res?.data?.active?.data?.presets)).catch(() => undefined);
  }, [canEnterOnlineReward, month]);

  if (loaded && !canViewOnlineReward) return <NoAccess />;

  const act = (promise: Promise<unknown>, ok: string) =>
    promise
      .then(() => {
        notification.success({ message: ok });
        setSelected([]);
        load();
      })
      .catch((err) => notification.error({ message: 'Chưa làm được', description: rewardErrorMessage(err) }));

  const decideMany = (decision: 'CONFIRMED' | 'REJECTED') =>
    Modal.confirm({
      title: `${decision === 'CONFIRMED' ? 'Xác nhận' : 'Từ chối'} ${selected.length} khoản?`,
      okText: decision === 'CONFIRMED' ? 'Xác nhận' : 'Từ chối',
      cancelText: 'Huỷ',
      onOk: () =>
        AxiosUtils.post(`${API.onlineRewardItems}/decide`, { ids: selected, decision })
          .then((res: any) => {
            const r = res?.data || {};
            notification.info({ message: `Xong ${r.done?.length ?? 0} khoản${r.skipped?.length ? `, bỏ qua ${r.skipped.length}` : ''}` });
            setSelected([]);
            load();
          })
          .catch((err) => notification.error({ message: 'Chưa làm được', description: rewardErrorMessage(err) })),
    });

  const canEdit = (it: RewardItem) => it.status === 'PENDING' && canEnterOnlineReward && (isAdmin || it.createdById === userId);

  const columns: ColumnsType<RewardItem> = [
    { title: 'Creator', key: 'creator', width: 190, render: (_, it) => <div className={styles.creator}><strong>@{it.tiktokUsername || it.tiktokUid}</strong><span className={styles.linked}>{it.tiktokUid}</span></div> },
    { title: 'Loại', dataIndex: 'type', width: 160, render: (t: RewardItem['type']) => ITEM_TYPE_LABEL[t] },
    {
      title: 'Số tiền / trừ', key: 'value', width: 160, align: 'right',
      render: (_, it) => (it.type === 'LIVE_DEDUCT' ? `−${it.deductDays || 0} ngày, −${hoursText(it.deductSeconds || 0)}` : it.type === 'VPNT' ? 'Cả tháng 0 đ' : <span className={styles.money}>{vnd(it.amount)}</span>),
    },
    { title: 'Ngày', dataIndex: 'eventDate', width: 100, render: (d: string | null) => (d ? dayjs(d).format('DD/MM') : '—') },
    {
      title: 'Ghi chú', key: 'note', ellipsis: true,
      render: (_, it) => (
        <span>
          {it.note || '—'} {it.evidenceUrl && <a href={it.evidenceUrl} target="_blank" rel="noreferrer">Minh chứng</a>}
        </span>
      ),
    },
    { title: 'Trạng thái', dataIndex: 'status', width: 120, render: (s: RewardItem['status']) => <Tag color={ITEM_STATUS_COLOR[s]}>{ITEM_STATUS_LABEL[s]}</Tag> },
    { title: 'Người nhập', dataIndex: 'createdByName', width: 130, ellipsis: true },
    { title: 'Người duyệt', dataIndex: 'decidedByName', width: 130, ellipsis: true },
    {
      title: 'Thao tác', key: 'actions', width: 230, fixed: 'right',
      render: (_, it) => (
        <Space size={4} wrap>
          {isAdmin && it.status === 'PENDING' && (
            <>
              <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => act(AxiosUtils.post(`${API.onlineRewardItems}/${it.id}/confirm`, {}), 'Đã xác nhận')}>Xác nhận</Button>
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => act(AxiosUtils.post(`${API.onlineRewardItems}/${it.id}/reject`, {}), 'Đã từ chối')}>Từ chối</Button>
            </>
          )}
          {canEdit(it) && (
            <>
              <Button size="small" onClick={() => { setEditing(it); setFormOpen(true); }}>Sửa</Button>
              <Button size="small" onClick={() => act(AxiosUtils._delete(`${API.onlineRewardItems}/${it.id}`, {}), 'Đã huỷ khoản')}>Huỷ</Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        title="Khoản nhập tay"
        extra={
          <Space wrap>
            <DatePicker picker="month" allowClear={false} format="MM/YYYY" value={dayjs(`${month}-01`)} disabledDate={(d) => d.format('YYYY-MM') > currentUtcMonth()} onChange={(d) => d && setParam('month', d.format('YYYY-MM'))} />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Tải lại</Button>
            {canEnterOnlineReward && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setFormOpen(true); }}>Thêm khoản</Button>
            )}
          </Space>
        }
      />
      {error && <Alert className={styles.section} type="error" showIcon message={error} />}
      <div className={styles.filters}>
        <Input.Search allowClear placeholder="Lọc theo UID" defaultValue={uid || ''} onSearch={(v) => setParam('uid', v.trim() || null)} style={{ width: 240 }} />
        <Select allowClear placeholder="Trạng thái" value={status} onChange={setStatus} style={{ width: 160 }} options={Object.entries(ITEM_STATUS_LABEL).map(([value, label]) => ({ value, label }))} />
        <Select allowClear placeholder="Loại" value={type} onChange={setType} style={{ width: 190 }} options={ITEM_TYPES.map((t) => ({ value: t, label: ITEM_TYPE_LABEL[t] }))} />
        {isAdmin && selected.length > 0 && (
          <Space>
            <Button type="primary" onClick={() => decideMany('CONFIRMED')}>Xác nhận {selected.length} khoản</Button>
            <Button danger onClick={() => decideMany('REJECTED')}>Từ chối {selected.length} khoản</Button>
          </Space>
        )}
      </div>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={items}
        scroll={{ x: 1400 }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} khoản` }}
        rowSelection={isAdmin ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as number[]), getCheckboxProps: (it) => ({ disabled: it.status !== 'PENDING' }) } : undefined}
      />
      <ItemFormModal
        open={formOpen}
        month={month}
        initial={editing}
        presetUid={uid}
        isAdmin={isAdmin}
        creators={creators}
        presets={presets}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); load(); }}
      />
    </>
  );
};

export default OnlineRewardItemsPage;
